import {
    UnprocessedOrderType,
    OrderStateTypes,
    OrderBusinessType,
    OrderStatusCode,
    LogisticsStatusCode,
    PayStatusCode,
    LogisticsTypeCode,
    SelfServiceCode,
    PayTypeCode,
    RawOrderBaseInfo,
    AllCountInfo,
    DispatchExceptionCode,
} from '../shared/types';

enum OrderBlockTag {
    /** 基础模块 */
    ORDER_INFO = 'orderInfo',
    /** 用户信息 */
    USER_INFO = 'userInfo',
    /** 商品信息 */
    FOOD_INFO = 'foodInfo',
    /** 结算信息 */
    CHARGE_INFO = 'chargeInfo',
    /** 配送信息 */
    LOGISTICS_INFO = 'logisticsInfo',
    /** 出餐信息 */
    MEAL_INFO = 'mealInfo',
    /** 出餐异常信息 */
    MEAL_ABNORMAL_INFO = 'mealAbnormalInfo',
    /** 退款信息 */
    REFUND_INFO = 'refundInfo',
    /** 餐损信息 */
    CANSUN_INFO = 'cansunInfo',
    /** 定责信息 */
    RESPONSIBLE_INFO = 'responsibleInfo',
    /** 用户赔付 */
    USER_COMPENSATE_INFO = 'userCompensateInfo',
    /** 催单 */
    REMINDER_INFO = 'reminderInfo',
    /** 食安信息 */
    FOOD_SAFE_INFOS = 'foodSafeInfos',
    /** 补送的售后状态变更 */
    RESEND_MEAL_AFTER_SALE_STATUS_CHANGE_INFO = 'resendMealAfterSaleStatusChangeInfo',
}

/** 取消险建议模块 */
type OrderCancelInsuranceSuggest = {
    /** 标签 */
    tag: string;
    /** 标题 */
    title: string;
    /** 建议详情 */
    detail: string;
    /** 链接文字 */
    linkName: string;
    /** 跳转链接 */
    linkUrl: string;
    /** 是否展示 */
    isShow: boolean;
};

/**
 * 订单详情
 * @wiki https://km.sankuai.com/page/1321468404
 */

type OrderDetailInfo = {
    /** 订单类型 */
    businessType: OrderBusinessType;
    /** 接口下发时的时间 */
    serverTime: number;
    /** 订单基础信息 */
    unifiedOrderInfo: OrderBasicInfo;
    /** 订单基础信息 */
    orderInfo: OrderBasicInfo;
    /** 用户信息 */
    userInfo: OrderUserInfo;
    /** 商品模块 */
    foodInfo: FoodInfo;
    /** 结算模块 */
    chargeInfo: ChargeInfo;
    /** 出餐信息 */
    mealInfo: OrderMealInfo;
    /** 出餐异常申诉信息 */
    mealAbnormalInfo: OrderMealAbnormalInfo;
    /** 退款模块 - 注意，可能为null */
    refundInfo: RefundInfo | null;
    /** 定责模块 - 注意，可能为null */
    responsibleInfo: ResponsibleInfo | null;
    /** 食安模块 */
    foodSafeInfos?: FoodSafeInfo[];
    /** 用户赔偿 */
    userCompensateInfo: UserCompensateInfo;
    /** 催单 - 注意，可能为null */
    reminderInfo: ReminderInfo | null;
    /** 餐损赔付 */
    cansunInfo: CansunInfo | null;
    /** 配送信息 */
    logisticsInfo: LogisticsInfo;
};

/**
 * 订单基础信息
 */
type OrderBasicInfo = {
    groupBuyingVo: {
        /** 是否展示社区拼团标题 */
        isShow: boolean;
        /** 社区团购订单提示文案 */
        groupBuyingTip: string;
        /** 社区团购当前拼团的订单流水号,包含当前订单 */
        groupBuyingDaySeqs?: number[];
    } | null;
    /** 订单流水号 */
    dayseq: number;
    /** 订单卡片右上角角标文案，目前用于展示预订单标识 */
    wordLogo: string;
    /** 订单状态描述文案 */
    statusDesc: string;
    /** 预计送达时间 ，显示：前缀 + 时间 + 后缀 */
    expectTimeVo: {
        /** 预计送达摘要前缀 */
        expectPrefix: string;
        /** 预计送达时间 */
        expectTimeFmt: string;
        /** 预计送达摘要后缀 */
        expectSuffix: string;
        /** 跑腿-预计送达摘要类型，默认空串。"1":众包即时单弹框, "2":众包预约单弹框 */
        expectType: '' | '1' | '2';
        /** ETA 修改成功后，原 ETA 字符串 */
        expectTimeBeforeModify: string;
        /** ETA 修改成功或失败后的icon解释文案 */
        expectTimeBeforeModifyTips: string;
    };
    /** 准时宝 */
    onTimeInsuranceVo: {
        /** 名称 */
        name: string;
        /** 状态描述 */
        desc: string;
        /** 状态码 */
        status: number;
        /** 是否展示  1展示 0不展示 */
        effect: 1 | 0;
    };
    /** 配送超时描述，目前仅APP使用 */
    deliveryTimeoutDesc: string;
    /** 订单标签类型 */
    orderTag: OrderTagCode;
    /** 订单标题提示信息 */
    tipInfoVo: {
        /** 提示类型  1.提醒 2.警告 */
        tipType: 0 | 1 | 2 | 3;
        /** 提示文案 */
        tipDesc: string;
        deadlineTime: number;
        /** 是否展示 */
        isShow: boolean;
    };
    /** 订单viewId */
    wmOrderViewId: string;
    /** 订单Id */
    wmOrderId: string;
    /** 下单时间字符串 */
    orderTimeFmt: string;
    /** 放心吃标签文案 */
    foodSafeEatInsuranceDesc: string;
    /** 显示取消订单和接单按钮 */
    isShowConfirmAndCancelButton: boolean;
    /** 是否展示取消订单按钮 */
    isShowOrderCancelButton: boolean;
    /** 取消订单 VO */
    orderCancelButtonVo: {
        /** 0：不展示；1：置灰；2：正常展示 */
        status: 0 | 1 | 2;
        /** 置灰提示文案 */
        toastDesc: string;
    };
    /** 是否展示部分退款按钮 */
    isShowPartRefundButton: boolean;
    /**
     * APP订单卡片底部是否展示“送餐”按钮（没有配送服务时展示）
     * PC用于纯自配商家的“自己送餐”按钮展示
     */
    isShowDeliveryButton: boolean;
    isShowCustomerServiceIcon: boolean;
    basicVo: {
        /** 是否预订单 */
        isPreOrder: boolean;
        /** 订单状态 */
        status: OrderStatusCode;
        /** 下单时间戳 */
        orderTime: number;
        /** 预计送达时间戳 */
        estimateArrivalTime: number;
        /** 确认订单时间戳 */
        confirmTime: number;
        /** 是否团餐订单 */
        isGroupOrder: boolean;
        /** 门店坐标经度 */
        longitude: number;
        /** 门店坐标纬度 */
        latitude: number;
        /** 收餐坐标经度 */
        addressLongitude: number;
        /** 收餐坐标纬度 */
        addressLatitude: number;
        inArea: number;
        /** 配送开始时间戳 */
        deliveryBtime: number;
        deliveryBtimeFmt: string;
        /** 订单支付类型 */
        payType: PayTypeCode;
        /** 订单支付状态 */
        payStatus: PayStatusCode;
        /** 取餐类型 1-到店取餐 0-堂食 */
        pickType: 0 | 1;
        /** 当前服务器时间 */
        serverTime: number;
        /** 门店id */
        wmPoiId: number;
        /** 是否无人机配送 */
        isUavDelivery: boolean;
        /** 是否SLA门店 */
        isSla: boolean;
    };
    /** 是否需要餐具 */
    isNeedTableware: boolean;
    /** 门店名称 */
    poiName: string;
    /** 门店所在城市名称 */
    cityName: string;
    /** 复制订单订单按钮 */
    copyButtonVo: {
        /** 是否展示按钮 */
        isShow: boolean;
        /** 需要复制的内容文案 */
        clickCopyContent: string;
    };
    /** 小票打印计数 */
    printCount: number;
    /** 门店接单类型 */
    selfService: SelfServiceCode;
    /** 极速达备餐号 */
    jsdOrderInfoVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 展示文案 */
        context: string;
        /** 备餐号 */
        mealNumber: string;
    };
    etaModifyVo?: {
        /** 是否展示eta修改申请模块 */
        isShow: boolean;
        /** 用户发起ETA修改的时间，格式为MM-DD hh:mm */
        uetaCtime: string;
        /** 原订单ETA日期，格式为MM-DD */
        etaTimeDate: string;
        /** 原订单ETA时间，格式为hh:mm */
        etaTimeDay: string;
        /** 原ETA订单的预订单标识，在原时间后方展示【预】标识 */
        wordLogo: string;
        /** 用户期望的新ETA日期，格式为MM-DD */
        uetaTimeDate: string;
        /** 用户期望的新ETA时间，格式为hh:mm */
        uetaTimeDay: string;
        /** 用户申请修改eta订单的预订单标识，在新时间后方展示【预】标识 */
        uetaWordLogo: string;
        /** 时间变更描述  延后/提前 */
        uetaModifyTimeGapDesc: string;
        /** 新eta时间同原eta时间间隔 用新eta-旧eta的绝对值判断，60min以内展示xxmin；24h以内展示xxhxxmin；超过24h则展示xx天（x换算成天） */
        uetaModifyTimeGap: string;
        /** 截止时间，时间戳，用于倒计时 */
        uetaExpire: number;
        /** 审核状态 1.申请，2.通过，3.商家拒绝，4.超时未处理，5.订单完成或取消自动拒绝 6.订单处于退款中自动拒绝 7.商家点击平台原因拒绝（骑手已接单、骑手已到店、骑手已取餐、骑手已送达）8.商家点击平台原因拒绝（跑腿自动锁单状态）9.商家点击平台原因拒绝（美团直送）)*/
        uetaStatus: number;
        /** 是否展示【同意修改】按钮 */
        agreeEtaModifyBtn: boolean;
        /** 是否展示【拒绝修改】按钮 */
        refuseEtaModifyBtn: boolean;
        /** eta修改后，判断是否是跨天订单 */
        isMoreThanOneDay: boolean;
        /** eta系统自动处理的倒计时文案 */
        etaModifyDescSuffix: string;
    };
    /** ETA修改提示条 */
    etaAutoAgreeTips: {
        isShow: boolean;
        desc: {
            text: string;
            /** 默认0-无样式，1-高亮，2-红色颜色，3-换行 */
            textShowType: 0 | 1 | 2 | 3;
        }[];
    };
    /** 保险标签 */
    insuranceList?: {
        /** 保险名称 */
        name: string;
        /** 保险描述 */
        desc: string;
        /** 保险状态 */
        status: number;
        /** 是否生效：1 生效，0 失效 */
        effect: 1 | 0;
        /** 理赔详情文案 */
        claimDesc: string;
    }[];
    /** 神抢手订单标志 */
    isSharpShooter: boolean;
    /** 商企通订单 */
    sqtOrder: boolean;
    /** 商企通订单需开发票 */
    needSqtInvoice: boolean;
    /**订单类型 0：普通外卖订单  10: 拼好饭单买订单 20：拼好饭拼单-主单 30：拼好饭拼单-子单*/
    orderType: number;
    /** 异常订单标志，值为1：预计收入为0 */
    abnormalOrder: 0 | 1;
    /** 异常订单提示文案 */
    orderConfirmButtonInfoVo: {
        /** 是否提示 */
        isAlarm: boolean;
        title: string;
        content: string;
    };
};
enum OrderTagCode {
    /** 无标签 */
    NONE = 0,
    /** 全城送 */
    QUAN_CHENG = 1,
    /** 到店自取 */
    SELF_PICK = 2,
    /** 大额到付 */
    DA_E_DAO_FU = 3,
    /** 大订单 */
    BIG_ORDER = 4,
    /** 大额订单 */
    DA_E = 5,
    /** 货到付款 */
    PAY_ON_RECEIVE = 6,
    /** 环保单 */
    ECO = 7,
    /** [优享送](https://km.sankuai.com/collabpage/1512345263) */
    YXS = 8,
    /** 超区送 */
    EXCEED_AREA = 9,
    /** 无人机*/
    UAV = 10,
    /** 20min专区*/
    ZONE_20_MIN = 11,
}

/**
 * 用户信息
 */
type OrderUserInfo = {
    /** 顾客名称 */
    recipientName: string;
    /** 隐私号录音提示文案 */
    recipientPhoneTipVo: {
        /** 隐私号类型  1大陆，2港澳台 */
        phoneType: 1 | 2;
        /** 提示文案 */
        tipDesc: string;
        /** 是否展示 */
        isShow: boolean;
    };
    /** 是否展示IM发起聊天按钮 */
    isShowIMIcon: boolean;
    /** 用户ID */
    wmUserId: number;

    /** 顾客电话 */
    recipientPhoneVo: {
        pcRecipientPhoneVo: {
            pcPhoneVos: Array<{
                /** 电话类型 */
                title: string;
                /** 电话列表 */
                phones: string[];
                /** 顾客电话是否加密  3-加密 */
                encrypted: number;
                clickShowPhoneButtonVo: {
                    isShow: boolean;
                    showPhoneKey: string;
                };
            }>;
        };
    };
    /** 收餐地址信息 */
    customerAddressVo: {
        isShow: boolean;
        recipientAddressVo: {
            pcRecipientAddressVo: {
                /** 是否默认隐藏 */
                isHide: boolean;
                /** 顾客地址 */
                recipientAddress: string;
            };
        };
        /** 是否展示地图入口 */
        isShowMap: boolean;
        distanceAndAddressVo: {
            /** 收餐地址坐标经度 */
            addressLongitude: number;
            /** 收餐地址坐标经度 */
            addressLatitude: number;
            /** 配送距离 */
            orderDistance: number;
            /** 门店坐标经度 */
            poiLongitude: number;
            /** 门店坐标纬度 */
            poiLatitude: number;
        };
    };
};

/** 出餐模块主体展示信息  */
type FoodDoneInfo = {
    /** 本状态开始时间戳，不用于展示内容的计时目标 */
    btime: number;
    /** 0：预定单；1：待出餐；2：即将超时；3：已超时；4：已出餐&w未上报出餐 */
    showType: 0 | 1 | 2 | 3 | 4;
    title: string;
    timeText: string;
    label: {
        /** 标签样式 */
        type: 1 | 2 | 3 | 4;
        /** 标签内容文本 */
        text: string;
    }[];
    subTitle: {
        /** 考核时间文本说明 */
        examineTimeText: string;
        /** 考核时间格式化 */
        examineTimeFmt: string;
        /** 标签列表 可以多个并排 */
        label: {
            /** 标签样式 */
            type: 1 | 2 | 3 | 4;
            /** 标签内容文本 */
            text: string;
        }[];
        /** 是否展示问号提示及跳转 */
        showTip: boolean;
        /** 考核说明文案 */
        instruction: string;
        /** 展示异常提示 */
        isException: boolean;
        /** 异常帮助文案 */
        exceptionDesc: string;
    };
};

/** 新版出餐模块信息 */
type FoodDoneExamineVo = {
    /** 订单出餐状态    1：待出餐  2：已出餐   3：未出餐（且不能再出餐） */
    foodDoneStatus: number;
    /** 出餐考核时间戳 */
    examineTime: number;
    foodDoneInfo: FoodDoneInfo[];
    foodDoneButtonVo: {
        /** 是否展示按钮 */
        isShow: boolean;
        buttonName: string;
        buttonFoldName: string;
        onButtonTip: string;
    };
};

/** 出餐信息 */
type OrderMealInfo = {
    /** 是否展示出餐模块 */
    isShow: boolean;
    /** 1：新版出餐模块; 0：旧版出餐模块 */
    version: 0 | 1;
    /** 新版出餐模块信息 */
    foodDoneExamineVo: FoodDoneExamineVo;
    /** 出餐状态标题 */
    foodDoneTitleVo: {
        /**
         * 此订单备餐标题是否可能会发生变化
         * 如果不会变化（普通订单）则始终展示title字段
         * 如果会变化（预订单）则当前时间 > reminderTime时展示 changeTitle，否则展示 title。
         */
        isChange: boolean;
        title: string;
        changeTitle: string;
        /** 预订单，标题变化的时间戳 */
        reminderTime: number;
    };
    /** 倒计时信息 */
    countDownTimerVo: {
        isShowVo: {
            isToShow: boolean;
            preRemindTime: number;
        };
        timerShowVo: {
            /** 是否出餐完成 */
            isFoodDone: boolean;
            /** 出餐时间字符串（已出餐状态展示） */
            preMealTimeIntervalFmt: string;
            /** 备餐开始时间戳（ 预订单，非预定单，该字段含义不一致，但前端无需关注） */
            preMealBeginTime: number;
            /** 结束备餐 ETA，超过此时间则展示超时提示文案 */
            preMealEndTime: number;
        };
    };
    /** 出餐提示 */
    foodDoneTip: string;
    /** 出餐完成按钮 */
    foodDoneButtonVo: {
        /** 按钮是否显示 */
        isShow: boolean;
        /** 小按钮文案 */
        buttonName: string;
        /** 大按钮文案 */
        buttonFoldName: string;
        /** 按钮提示 */
        onButtonTip: string;
        /** 点击按钮 */
        buttonClickVo: {
            /** 此时间节点之后按钮才可以点击 */
            canClickLimitTime: number;
            /** 不满足条件提醒，为空则发生点击行为 */
            clickWarningToast: string;
        } | null;
    };
    /** 出餐设备提示文案：出餐宝等 */
    foodSendDeviceInfo: string;
    mealTipInfoVo?: {
        /** 文案类型（0.无文案 1.提醒 2.警告 3.倒计时） */
        tipType: number;
        tipDescVo: {
            pcTipDesc: string;
        };
        deadlineTime: number;
        isToShow: boolean;
    };
    /** 跑腿出餐信息 */
    runLegVo: {
        /** 是否展示跑腿出餐模块 */
        isShow: boolean;
        /** 标题模块 */
        runLegTitleVo: {
            isToShow: boolean;
            title: string;
            serverTimeCompareToTitleVo: {
                runLegDelayAddConfirmTime: number;
                moreServerTimeTitle: string;
                lessServerTimeTitle: string;
            };
        };
        runLegTimerVo: {
            isToShow: boolean;
            timer: number;
            serverTimeCompareToTimerVo: {
                runLegDelayAddConfirmTime: number;
                moreServerTimeTimer: number;
                lessServerTimeTimer: number;
            };
        };
        runLegTipVo: {
            isToShow: boolean;
            tip: string;
            serverTimeCompareToTipVo: {
                runLegDelayAddConfirmTime: number;
                moreServerTimeTip: string;
                lessServerTimeTip: string;
            };
        };
        runLegButtonVo: {
            isToShow: boolean;
            buttonName: string;
            serverTimeCompareToButtonNameVo: {
                runLegDelayAddConfirmTime: number;
                moreServerTimeButtonName: string;
                lessServerTimeButtonName: string;
            };
        };
    };
};

/** 出餐异常申诉信息 */
type OrderMealAbnormalInfo = {
    /** 提醒文案信息 */
    appealInfoVo?: {
        /** 是否展示提示文案 */
        isShow: boolean;
        /** 提示类型， 1超时未申诉，0待申诉 */
        appealWarningType: 1 | 0;
        appealWarningContentVo: {
            /** 提示文案 */
            pcAppealWarningContent: string;
        };
        appealButtonVo: {
            /** 最后允许申诉的时间戳 */
            appealTimeLimit: number;
            /**  */
            appealTimeout: number;
        };
    };
    appealRecordVo?: {
        isShow: boolean;
        appealRecords: Array<{
            /** 申诉记录标题 */
            appealTitle: string;
            /** 申诉记录时间 */
            appealTime: string;
            /** 申诉记录图片列表 */
            appealPictures: string[];
            /** 申诉记录步骤 */
            appealStep: number;
            /** 申诉记录详情 */
            appealStepDetailVo: {
                /** 申诉详情文案 */
                appealStepDetail: string;
            };
        }>;
    };
};

/**
 * 商品信息 相关
 * @wiki https://km.sankuai.com/page/1317841491#id-2.7%E5%95%86%E5%93%81%E4%BF%A1%E6%81%AF
 * @Yapi https://yapi.sankuai.com/project/20566/interface/api/975929
 */

type FoodInfo = {
    /** 备注 */
    remark: string;
    /** 发票信息 */
    invoiceTitle: string;
    /** 税号 */
    invoiceTaxpayerId: string;
    /** 商品数量文案 */
    foodKindsAndNum: string;
    // /** 商品详情 */
    cartDetailVos: CartDetailInfo[];
    /** 用户标签信息 */
    userLabelVo: {
        isShow: boolean;
        contents: Array<{
            type: number;
            /** 标签文案 */
            info: string;
        }> | null;
    };
};
/** 商品详情 */
type CartDetailInfo = {
    /** 篮子名称 */
    cartName: string;
    /** 篮子内的菜品详情 */
    details: FoodCartDetailInfo[];
};
/** 商品篮子详情 */
export type FoodCartDetailInfo = {
    /** 所属篮子 id */
    cartId: number;
    foodId: number;
    /** 菜品名称 */
    foodName: string;
    /** 菜品单价 折扣价 */
    foodPrice: number;
    /** 已点份数 */
    count: number;
    /** 菜品原价 */
    originFoodPrice: number;
    /** 折扣商品的打折数 */
    discountPoint: string;
    /** 套餐 */
    subDetails: {
        /** 套餐餐品名 */
        foodName: string;
        /** 数量 */
        count: number;
    }[];
    /**
     * 商品库3.0的规格属性小料信息
     */
    productV3Details: string;
    /** 是否是神抢手商品 */
    isSharpShooter: boolean;
    /** 神枪手一口价订单标志  1:是 */
    sharpShooterTag: number;
};

/**
 * 结算信息 相关
 * @wiki https://km.sankuai.com/page/1317841491#id-2.8%E7%BB%93%E7%AE%97%E4%BF%A1%E6%81%AF
 * @Yapi https://yapi.sankuai.com/project/20566/interface/api/983157
 */
type ChargeInfo = {
    /** 固定结算信息(包含降级和非降级的固定结算信息) */
    fixedSettlementInfo: {
        /** 餐盒费总价 */
        boxpriceTotal: number;
        /** 小计[商品总额+包装费+打包费] */
        foodAmount: number;
        /** 降级后赠品信息 - 未使用 */
        discounts: {
            category: number;
            type: string;
            /** 优惠信息描述 */
            info: string;
            /** 展示类型 0全部展示 1PC端不展示 2客户端不展示 3打印机不展示 */
            hiddenType: number;
        }[];
        /** 降级后用户支付配送费 */
        shippingFee: number;
        /** 活动总额 */
        activityAmount: string;
        /** 活动详情 */
        activityDetails: ChargeActivityDetail[];
        /** 佣金总额title */
        formulaDisplayTitle: string;
        /** 计算公式 */
        formulaDisplay: string;
        /** 本单预计收入 */
        commisionAmount: number;
        /** 本单平台收入 */
        offlineOrderSkPayAmount: number;
        /** 预判配送方式 */
        shippingType: string;
        /** 佣金详情 */
        commisionDetails: ChargeCommisionDetail[];
        /** 降级后用户在线支付，优惠后的金额[用户在线支付]=商品总额+配送费+包装-活动款 */
        userOnlinePayAfter: number;
        /** 是否降级 */
        isDowngrade: boolean;
        /** 在线支付用户实际支付 */
        userPayTotalAmount: number;
        /** 骑手应付款 */
        riderPayment: string;
        /** 是否是线下（骑手）垫付 */
        isRiderPay: boolean;
        /** 赠品 */
        giftDetails: null | ChargeGiftDetail[];
        /** 货到付款，用户应付 */
        userPayAmount: number;
        /** 捐赠金额 */
        donateAmount: number;
        /** 是否预计收入降级 */
        settleAmountDegrade: string;
        /** 打包袋价格(单位元) */
        packageBagMoney: number;
        /** 预计收入降级文案内容 */
        orderSettleName: string;
        /** 结算付款金额 */
        orderSettleAmount: string;
        /** 结算付款金额 */
        settleAmount: number;
        /** 是否显示卡片折叠时预计收入中的“¥” */
        orderSettleDollar: boolean;
        /** 用户区块展示的结算信息 */
        settleAmountInfo: string;
    };
    /** 结算信息动态化展示 */
    settleDynamic: ChargeSettlementItem[];
};
/** 结算-活动详情 */
type ChargeActivityDetail = {
    /** 活动金额 */
    chargeAmount: string;
    /** 金额描述 */
    chargeDesc: string;
};
/** 结算-佣金详情 */
type ChargeCommisionDetail = {
    /** 佣金金额 */
    chargeAmount: string;
    /** 佣金描述 */
    chargeDesc: string;
};
type ChargeGiftDetail = {
    /** 赠品类型 */
    typeDesc: string;
    /** 赠品名字 */
    name: string;
    /** 赠品成本价 */
    amount: number;
    /** 赠品数量 */
    num: number;
};

type ChargeSettlementTag = {
    isShow: boolean;
    appStyleId: number;
    pcStyleId: string;
    /** 标签文案 */
    tagDesc: string;
};

type PopupDiscountInfo = {
    isShow: boolean;
    popupDiscountTag: ChargeSettlementTag;
    disCountDesc: string;
};

type PopupBottomInfo = {
    isShow: boolean;
    isShowTagIcon: boolean;
    isSharpShooter: boolean;
    prefixMsg: string;
    amountMsg: string;
    suffixMsg: string;
    markType: number;
    appUrl: string;
    pcUrl: string;
    popupWindow: {
        /** 弹框标题 */
        title: string;
        /** 弹框内容, eg： 天天神券是指...提报的额度。如需调整活动可点击{{xxx}} */
        content: string;
        /** 弹框内容链接解析配置 */
        parsingRule: Array<{
            /** 对应上面例子中的 xxx */
            key: string;
            /** xxx 要被替换为的文案 */
            name: string;
            /** 点击 xxx 要跳转的链接地址 */
            uri: string;
        }>;
    };
};

/** 结算信息项 */
type ChargeSettlementItem = {
    /** 是否展示问号 */
    showRemind: boolean;
    /** hover 问号时展示的文案(目前仅有青山计划用到) */
    toast: string;
    /** 收费项名称 */
    chargeName: string;
    /** 收费项金额 */
    totalCharge: string;
    /** 弹窗的总收费名称 */
    popupTotalChargeName: string;
    /** 弹窗收费的总金额 */
    popupTotalCharge: string;
    /** pc-收费规则 */
    pcChargeRules: string;
    /** app-收费规则 */
    appChargeRules: string;
    /** 收费规则对应的名称 */
    chargeRulesName: string;
    /** 收费项明细列表 */
    item: ChargeItem[];
    appStyleId: string;
    /** 样式id */
    pcStyleId: string;
    /** 弹窗收费的(副)总金额，主要展示划线价 */
    popupTotalChargeExtension: string;
    /** 弹窗收费的(副)总金额样式， 0为正常格式，1为中划线*/
    popupTotalChargeExtensionStyleId: string;
    chargeNameExtendIcon?: number;
    /** 收费项扩展文案（红色高亮） */
    chargeNameExtend?: string;
    /** app用 0-flutter 1-h5 */
    chargeRulesType: 0 | 1;
    /** 费用计算公式 */
    calculationRules: string;
    /** 问号展示的位置，0跟在收费名称后边，1跟在费用后边 */
    showRemindStyle: 0 | 1;
    /** 分割线样式，0不分割，1上方分隔，2下方分隔，3上下都分隔 */
    separateStyle: 0 | 1 | 2 | 3;
    /** 弹框标题文案（如果有） */
    popupTitle: string;
    /** 弹框副标题 */
    popupSubTitle: string;
    /** 弹窗收费的总金额的子项 */
    popupTotalChargeItems: {
        chargeDetailName: string;
        chargeDetailMoney: string;
    }[];
    /** 开具发票提醒文案 **/
    invoiceTip: string;
    /** 佣金标签列表字段 */
    tagList: ChargeSettlementTag[];
    /** 佣金合计下折扣标签和文案 */
    popupDiscountInfo: PopupDiscountInfo;
    /** 佣金弹窗-底部提示条 */
    popupBottomInfo: PopupBottomInfo;
};
type ChargeItem = {
    /** 收费项名称 */
    chargeDetailName: string;
    /** 收费项金额 */
    chargeDetailMoney: string;
    /** 样式ID */
    styleId: string;
    /** 嵌套的子收费项 */
    item?: ChargeItem[];
    /** 收费项名称后是否展示问号icon */
    showPopover: boolean;
    /** 问号icon弹框内容 */
    popover: {
        /** 弹框标题 */
        title: string;
        /** 弹框内容, eg： 天天神券是指...提报的额度。如需调整活动可点击{{xxx}} */
        content: string;
        /** 弹框内容链接解析配置 */
        parsingRule: Array<{
            /** 对应上面例子中的 xxx */
            key: string;
            /** xxx 要被替换为的文案 */
            name: string;
            /** 点击 xxx 要跳转的链接地址 */
            uri: string;
        }>;
    };
    /** 神抢手订单标志 **/
    isSharpShooter: boolean;
    /** 神枪手一口价订单标志  1:是 */
    sharpShooterTag: number;
};

/**
 * 退款 相关
 * @wiki https://km.sankuai.com/page/1317841491#id-2.11%E9%80%80%E6%AC%BE%E4%BF%A1%E6%81%AF
 * @Yapi https://yapi.sankuai.com/project/20566/interface/api/983678
 */
type RefundInfo = {
    /**
     * 订单卡片上的新版的待处理退款节点需要的信息从此处取，可忽略 `refundRecords[0]`。
     *
     * 参见[技术方案](https://km.sankuai.com/collabpage/2055705731)
     */
    waitingRefundVo?: WaitingRefundVo;
    /** 退款建议 */
    refundSuggestionVo: RefundSuggestionVo;
    /** 退款记录 */
    refundRecords: RefundRecordItem[];
    /** 售后进度 */
    saleSchedules: {
        /** 进度名称 */
        name: string;
        /** 进度状态 */
        status: SaleSchedulesStatus;
    }[];
    /** 预判责按钮VO */
    preResponsibilityButtonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮文案 */
        title: string;
    };
    /** 预判责原因 */
    preResponsibilityReasonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 原因内容 */
        title: string;
    };
    /** 用户取消险信息描述 */
    showInsuranceAdviceDesc: string;
    /** 预判责责任方 */
    preResponsibleParty: number;
    /** 是否展示退款建议 */
    showInsuranceAdvice: boolean;
    /** （SLA）售后灰度 */
    isSaleInfoGray: boolean;
    /** 退款基础信息 */
    refundBasicVo: {
        /** 是否部分退 */
        isPartRefundApply: boolean;
        /** 退款类型（0：按份退 1：按克重退） */
        refundType: 0 | 1;
    };
    insuranceAdviceSuggestVo: OrderCancelInsuranceSuggest;
    /** （美配，非 SLA）是否是新餐损 */
    isNewCansun: boolean;
    /** 大纠纷订单标识 */
    afterSaleIssueGray: boolean;
    /** 小美评审 */
    instantRefundJudgeInfo: InstantRefundJudgeInfo;
    /** 退款创建时间，用于模块排序 */
    sortSecond: number;
    /** 退款模块样式的版本 */
    refundBlockVersion: 0 | 1;
    /** 大纠纷订单规则文案 */
    majorDisputeAuditInfo: string;
};

/** 餐损责任方 */
export enum ResponsibleParty {
    /** 用户 */
    User = 1,
    /** 商家 */
    Poi = 2,
    /** 骑手 */
    Rider = 3,
    /** 平台 */
    Platform = 4,
    /** 天气 */
    Weather = 5,
    /** 用户责任，但商家开通了取消险 */
    Insurance = 6,
    /** 共同担责 */
    Multiple = 7,
    /** 未知 */
    Unknown = 0,
}

export interface WaitingRefundVo {
    isShow: boolean;
    title: string;
    time: string;
    refundSelectSolution: {
        tips?: string[];
    };
    refundReason: Pick<RefundRecordItem['reasonVo'], 'reason' | 'label'> &
        Pick<RefundRecordItem, 'pictures'>;
    /** 退款截止时间、催审信息等 */
    refundButtonVo: RefundRecordItem['refundButtonVo'];
    /** 售后单id afterSaleOrderId */
    refundViewId?: string;
    /** 预判责信息 */
    preConfirmResponsibilityResult: {
        title: {
            /** 形如「预估为{#responsibleParty}，退款后{#responsibilityResult}」 */
            content: string;
            responsibleParty: string;
            responsibilityResult: string;
            /** 商家部分原因解释弹窗内容 */
            titleTips?: string[];
        };
        subtitle: {
            content: string;
            compensateInfo: string;
        };
    };
    /** 预判责的责任方，前端用于控制样式 */
    responsiblePartyVo: {
        code: ResponsibleParty;
        /** 责任方 */
        responsibleParty: string;
    };
    /** 是否展示用户 IM */
    showUserIMIcon: boolean;
    /** 查看骑手电话 */
    riderPhoneIconVo: LogisticsInfo['phoneIconVo'];
    /** 二级页面的补充信息，包括退款建议、处理按钮等 */
    refundSecondaryPageExtraInfo: {
        /** 退款建议 */
        refundSuggestVo: {
            /** 责任方标题 */
            title: WaitingRefundVo['preConfirmResponsibilityResult']['title'];
            /** 骑手上报场景需要展示 */
            tip: string;
            /** 一个元素对应一个退款建议 */
            subtitle: WaitingRefundVo['preConfirmResponsibilityResult']['subtitle'][];
        };
        /** 退款处理方案 */
        refundSolutions: {
            /** 方案标题 */
            title: string;
            /** 方案标签 */
            labels: {
                /** 标签文案 */
                content: string;
                /**
                 * 样式类型
                 *
                 * - 0 绿色
                 * - 1 灰色
                 * - 2 红色
                 * - 3 橙色
                 */
                style: number;
            }[];
            /** 操作按钮 */
            operationButtons: {
                /**
                 * 按钮标识
                 *
                 * - 1 同意退款
                 * - 2 拒绝退款
                 * - 3 小额转账
                 * - 4 转部分退款
                 * - 5 转自配
                 * - 6 补发餐品
                 */
                code: number;
                /** 按钮文案 */
                content: string;
            }[];
        }[];
        /** 预计收入（发红包时展示用） */
        settleAmount: string;
        /** 发红包的最大金额 */
        maximumCompensationAmount: number;
    };
}

/** 小美评审 */
type InstantRefundJudgeInfo = {
    /** 是否展示小美评审 */
    isShow: boolean;
    /** 纠纷评审保障 */
    title: string;
    /** 售后记录id */
    afterSaleId: string;
    /** 申诉副标题 */
    subTitle: string;
    /** 申诉超时的时间戳（秒级时间戳） */
    timestamp: number;
    /** 账单时间（秒级时间戳） */
    billTime: number;
    content: {
        text: string;
        linkName: string;
        /** 1、 去申诉; 2、查看投票结果; 3、查看账单; 0、默认值，无需操作 **/
        linkType: 1 | 2 | 3 | 0;
        /** 2的时候是小美评审 h5  url 地址 */
        linkValue: string;
    }[];
};
/** 退款建议 */
type RefundSuggestionVo = {
    /** 是否展示退款建议 */
    isShow: boolean;
    /** 退款建议内容 */
    refundSuggestions: RefundSuggestions;
};
/** 退款建议内容 */
type RefundSuggestions = {
    /** 标题 */
    title: string;
    /** 退款建议 */
    detail: string[];
    /** 退款说明 */
    spec: {
        /** 说明名称 */
        specName: string;
        /** 说明url */
        specUrl: string;
        httpUrl: string;
        applyCompensateBtn: any;
    } | null;
};
/** 退款日志 */
type RefundRecordItem = {
    /** 退款标题 */
    title: string;
    /** 退款记录时间 */
    time: string;
    /** 退款记录时间戳 */
    sortTime: number;
    /** 退款原因 */
    reasonVo: {
        /** 原因标题 */
        reasonTitle: string;
        /** 原因内容 */
        reason: string;
        /** 退款标签 */
        label: string;
        /** 拒绝退款提醒 */
        rejectRefundRemind: string;
        /** 调用 `/gw/api/order/saleInfo/rules` 接口时传的参数 */
        sourceType: number;
        /** 链接标题 */
        specName: string;
    };
    /** 图片列表 */
    pictures: {
        /** 缩略图 */
        thumbnail: string;
        /** 原图 */
        originPicture: string;
    }[];
    /** 退款金额 */
    moneyVo: {
        /** 退款金额标题 */
        title: string;
        /** 退款金额 */
        money: number;
        /** 是否展示退款扣除商家收入 */
        showPartRefundItem: boolean;
        partRefundItems?: {
            partRefundName: string;
            partRefundExplanation: string;
            partRefundNameExtend: string;
            partRefundDisplayMoney: string;
            /** 1 是标题，0 是各费用项，2 是合计 */
            partRefundStyle: 0 | 1 | 2;
        }[];
    };
    /** 计价规则VO */
    priceRuleVo: {
        /** 计价标题 */
        title: string;
        /** 点击问号显示‘退款菜品计价规则’弹窗 */
        pr: string;
    };
    /** 退款餐品详细信息 */
    refundFoodItemInfolist: RefundFoodItemInfo[];
    /** 退款按钮 */
    refundButtonVo: {
        /** 是否展示退款按钮 */
        isShow: boolean;
        /** 退款截止时间 */
        agreeRefundDeadlineTime: number;
        /** 是否已催审 */
        isRefundUrge: boolean;
        /** 是否已压缩审核时长 */
        isCompressAuditTime: boolean;
        /** 催审弹框内容 */
        refundUrgeTips?: {
            title: string;
            tips: string;
        };
    };
    /** 处理类型code 0：等待处理，1：商家拒绝，2：商家同意，3：客户拒绝，4：客服同意，5：超时自动退款，6：系统自动通过，7：顾客取消退款申请，8：顾客取消退款申诉 */
    resType: number;
    /** 申请类型： 0、订单取消自动确认退款； 1、用户申请退款； 2、客服帮用户申请退款； 3、重复提交而自动申请； 4、支付成功消息在订单取消之后到达而自动申请； 5、支付成功消息在订单被置为无效之后到达而自动申请； 6、用户被商家拒绝后申诉 */
    applyType: number;
    /** 是否是大纠纷新增售后信息 */
    troubleImprove: boolean;
    /** 是否展示发红包按钮 */
    refundHongbaoBtn: boolean;
    /** 退款 id（发起 im 会话时需要） */
    refundViewId: string;
    rejectDialogInfo: {
        title: string;
        content: string;
    };
    /** 是否展示申诉按钮 */
    refundCanAppealBtn: boolean;
    /** 是否展示不申诉按钮 */
    refundNotAppealBtn: boolean;
    /** 平台处理时间是否是在订单完成之前。当平台处理完成时间 ＜= 订单完成时间时，不可申诉 */
    handleBeforeOrderComplete: boolean;
    /** 可以发起申诉剩余的时间，单位为秒 */
    refundAppealExpireTime: number;
    /**
     * 商家可退款申诉时的提示文案
     * e.g. 若您认为是用户原因，可发起退款申诉；若您认为是非用户原因，可选择认可平台处理，平台将继续进行餐损审核，如判定为骑手或平台原因，将按规则对您进行赔付。
     */
    aftersaleIssueContent: {
        text: string;
        linkName: string;
        linkType: number;
        linkValue: string;
    };
    /** 结算直赔对象信息 */
    aftersaleCompensateVO: {
        /** 直赔金额 */
        compensateMoney: number;
        /** 是否展示账单链接 */
        isShowCompensateBill: boolean;
    };
    /** 申诉图片 */
    appealPictures: string[];
    /** 售后记录 id，申诉接口需要传给后端 */
    afterSaleId: string;
    /** 节点类型，详见 [接口文档](https://km.sankuai.com/collabpage/1468981567#id-%E4%B8%89%E3%80%81%E6%8E%A5%E5%8F%A3%E6%96%87%E6%A1%A3) */
    nodeType: number;
    preAfterSaleInfo: {
        title: string;
        subTitle: string;
        pictures: string[];
    };
    /**
     *大纠纷退款申诉流程优化 申请赔偿
     */
    applyCompensateBtn: boolean;
};
/** 退款餐品详细信息 */
export type RefundFoodItemInfo = {
    /** 订单明细id */
    detailId: number;
    /** 商品id */
    wmFoodId: number;
    /** 商品梳理 */
    count: number;
    /** 退款金额 */
    refundPrice: number;
    /** 商品名 */
    foodName: string;
    /** 商品价格 */
    foodPrice: number;
    /** 是否展示价格 */
    showFoodPrice: boolean;
    /** 原价 */
    originFoodPrice: number;
    /** 包装费 */
    boxPrice: number;
    /** 包装盒数量 */
    boxNum: number;
    /** 商品图片 */
    picUrl: string;
    /** 克重退款单位 0:非克重 1：克 2：千克 */
    unitType: 0 | 1 | 2;
    /** 订单退款餐品详情 */
    priceDiffRefundList: {
        /** 退款重量 */
        refundWeight: number;
        /** 退款价格 */
        refundPrice: number;
    };
    /** 套餐子商品名称列表 */
    subFoodItemInfoList: {
        /** 套餐餐品名 */
        foodName: string;
        /** 数量 */
        count: number;
    }[];
    /** 商品库3.0 */
    productV3Details: string;
};
/** 售后进度状态 */
enum SaleSchedulesStatus {
    /** 待进行 */
    PENDING = 0,
    /** 进行中 */
    PROCESSING = 1,
    /** 已完成 */
    DONE = 2,
}

enum FoodSafeStatusIconType {
    /** 无 */
    None = 0,
    /** 放心吃 */
    EatEase = 1,
    /** 平台拦截 */
    PlatformInterception = 2,
}

enum FoodSafeLatestStatusCode {
    Processing = 0,
    Done = 1,
}
export type product = {
    name: string;
    num: string;
    /** 商品库3.0 */
    productV3Details: string;
    subFoodItemInfoList: {
        /** 套餐餐品名 */
        foodName: string;
        /** 数量 */
        count: number;
    }[];
};
type FoodSafeInfo = {
    /** 售后 id */
    afterSaleId: string;
    /** 保险id */
    insuranceId: string;
    /** 食安创建时间，用于模块排序 */
    sortSecond: number;
    /** 标题 */
    title: string;
    /** 放心吃提示信息 */
    eatEaseTips?: string;
    /** 状态描述 */
    statusDesc?: string;
    /** 状态描述图标 */
    statusDescIcon: FoodSafeStatusIconType;
    /** 用户申请问题 */
    userCompensateReason: string;
    /** 最新状态节点 */
    latestStatusVo: {
        isExist: boolean;
        /** 最新状态标题 */
        statusDesc: string;
        /** 最新状态描述 */
        statusSubDesc: string;
        /** 节点状态 */
        statusCode: FoodSafeLatestStatusCode;
    };
    /** 用户申请赔付详情 */
    userCompensateInfoVo: {
        isExist: boolean;
        title: string;
        /** 申请时间 */
        time: number;
        /** 倒计时结束时间 */
        deadline: number;
        /** 问题类型描述 */
        codeReason: string;
        /** 问题描述及顾客诉求 */
        applyReason: string;
        /** 问题商品 */
        products: product[];
        /** 问题图片 */
        pictures: string[];
    };
};

export enum ResponsibleLinkTipStatus {
    Default = 0,
    Success = 1,
    Fail = 2,
}

/**
 * 定责 - 模块
 * @wiki https://km.sankuai.com/page/1317841491#id-2.9%E5%AE%9A%E8%B4%A3%E4%BF%A1%E6%81%AF
 * @yapi https://yapi.sankuai.com/project/20566/interface/api/988003
 */
type ResponsibleInfo = {
    /** 定责详情 */
    responsibleInfos: ResponsibleInfos[];
    /** 定责申诉VO */
    responsibleAppealVo: ResponsibleAppealVo;
    /** 查看账单VO */
    viewBillButtonVo: ViewBillButtonVo;
    responsibleLinkTip?: {
        show: boolean;
        content: string;
        linkName: string;
        link: string;
        status: ResponsibleLinkTipStatus;
    };
};
/** 定责详情 */
type ResponsibleInfos = {
    /** 标题 */
    title: string;
    titleTips?: string[];
    /**
     * 定责状态
     *
     * 0位置 1定责中 2已定责不可申诉
     * 51已定责可申诉 52待申诉 53申诉超时 54已申诉 55待审核 56审核通过 57审核驳回
     * 81取消险已赔付 82取消险未赔付
     * 83赔付类型:直接赔付
     * 84赔付类型：完成率赔付 & 赔付状态：未处理
     * 85赔付类型：完成率赔付 & 赔付状态：不赔付
     * 86赔付类型：完成率赔付 & 赔付状态：已赔付
     * 87责任方：商家/用户/天气/未知 & 赔付状态：不赔付
     *
     
      * 定责状态
      * 
      * 0位置 1定责中 2已定责不可申诉
      * 51已定责可申诉 52待申诉 53申诉超时 54已申诉 55待审核 56审核通过 57审核驳回
      * 81取消险已赔付 82取消险未赔付
      * 83赔付类型:直接赔付
      * 84赔付类型：完成率赔付 & 赔付状态：未处理
      * 85赔付类型：完成率赔付 & 赔付状态：不赔付
      * 86赔付类型：完成率赔付 & 赔付状态：已赔付
      * 87责任方：商家/用户/天气/未知 & 赔付状态：不赔付
      * 
      */
    status: DUTY_STATUS;
    /** 当前状态的创建时间/赔付发起时间 */
    time: number;
    /** 当前状态的创建时间/赔付发起时间 */
    timeFormat: string;
    /** 描述 */
    desc: string;
    /** 规则名称 */
    specName: string;
    subDesc: {
        show: boolean;
        content: boolean;
        linkName: string;
        link: string;
        linkExpireDate: number;
        linkExpiredTip: string;
    };
    /** 账期类型 */
    periodType: number;
    /** 账期id */
    periodId: string;
    /** 保单id */
    insureOrderId: string;
    /**  */
    pictures: {
        /** 压缩后图片 */
        thumbnail: string;
        /** 原图 */
        originPicture: string;
    }[];
    /** 未理赔原因 */
    compensateResult: string;
    /** 取消险建议模块 */
    insuranceAdviceSuggestVo: OrderCancelInsuranceSuggest;
    /** 大纠纷二期-商责取消配送服务费子项 新增二级标题+链接 */
    responsibleDetailInfoList?: ResponsibleDetailInfoItem[];
};

/** 大纠纷二期-商责取消配送服务费子项 二级标题+链接 */
type ResponsibleDetailInfoItem = {
    /** 内容 */
    name: string;
    /** 链接（点击展开弹窗） */
    linkName?: string;
    /** 调用接口获取弹窗内容 */
    sourceType?: number;
};

/** 定责申诉 */
type ResponsibleAppealVo = {
    /** 是否展示申诉按钮 */
    isCanAppeal: boolean;
    /** 申诉倒计时 */
    deadlineTime: number;
    /** 可申诉文案 */
    canAppealDesc: string;
    /** 不可申诉文案 */
    canNotAppealDesc: string;
};
/** 查看账单 */
type ViewBillButtonVo = {
    /** 是否展示查看账单 */
    isShow: boolean;
    /** 按钮展示的文案 */
    context: string;
    /** 账期类型 */
    periodType: number;
    /** 账期id */
    periodId: string;
    /** 是否用户责任 */
    isUserResponsible: boolean;
    /** 用户责任定责时间 */
    userResponsibleTime: number;
};
/** 定责状态 */
enum DUTY_STATUS {
    /**未知 */
    UNKNOWN = 0, //
    /**定责中 */
    DUTYING = 1,
    /**已定责不可申诉 */
    DUTYED_NO_APPEAL = 2,
    /** 可申请餐损 */
    CAN_APPLY_FOR_COMPENSATION = 3,
    /** 不可申请餐损 */
    CANNOT_APPLY_FOR_COMPENSATION = 4,
    /** 超时未申请餐损 */
    APPLY_FOR_COMPENSATION_TIMEOUT = 5,
    /**已定责可申诉 */
    DUTYED_CAN_APPEAL = 51,
    /**待申诉 */
    PENDING_APPEAL = 52,
    /**申诉超时 */
    APPEAL_TIMEOUT = 53,
    /**已申诉 */
    APPEALED = 54,
    /**待审核 */
    PENDING_REVIEW = 55,
    /**审核通过 */
    REVIEW_PASS = 56,
    /**审核驳回 */
    REVIEW_REJECT = 57,
    /**取消险已赔付 */
    INSURANCE_PAY = 81,
    /**取消险未赔付 */
    INSURANCE_NO_PAY = 82,
    /**直接赔付 */
    COMPLETE_RATE_DIRECT_PAYMENT = 83,
    /**完成率赔付 & 赔付状态：未处理 */
    COMPLETE_RATE_NO_PROCESS = 84,
    /**完成率赔付 & 赔付状态：不赔付 */
    COMPLETE_RATE_NO_PAYMENT = 85,
    /**完成率赔付 & 赔付状态：已赔付 */
    COMPLETE_RATE_READY_PAYMENT = 86,
    /**责任方：商家/用户/天气/未知 & 赔付状态：不赔付 */
    NO_PAYMENT = 87,
}

/**
 * 用户赔偿 - 模块
 * @wiki https://km.sankuai.com/page/1317841491#id-2.12%E7%94%A8%E6%88%B7%E8%B5%94%E5%81%BF
 * @yapi https://yapi.sankuai.com/project/20566/interface/api/990032
 */
type UserCompensateInfo = {
    /** 赔偿标题 */
    title: string;
    /** 赔付成功发起时间（格式化后的MM-dd HH:mm） */
    time: string;
    /** 赔偿金额 */
    moneyDesc: string;
    /** 赔偿原因 */
    reasonDesc: string;
    /** 图片 */
    pictures: {
        /** 压缩图 */
        thumbnail: string;
        /** 原图 */
        originPicture: string;
    }[];
    /** 区块是否展示 */
    isShow: boolean;
    /** 用户赔偿申诉vo */
    appealInfoVo: {
        /** 是否展示申诉按钮 */
        isShow: boolean;
        /** 描述 */
        desc: string;
        /** 点击申诉后展示信息	 */
        appealInfo: string;
    };
    /** 赔偿规则 */
    compensateRules: {
        /** 退款规则 */
        refundRule: string[];
        /** 申诉方法及条件 */
        refundAppeal: string[];
        /** 扣款方式 */
        deductionType: string[];
        /** 申诉说明 */
        appealTips: string;
    };
    /** 赔付发起时间，用于模块排序 */
    sortSecond: number;
    /** 刷新订单倒计时，用于判断【去申诉>】按钮是否展示  */
    applyBtnRefreshTime: number;
    /** 申诉入口是否在灰度内 */
    applyEntranceGray: boolean;
};
type RulesInfo = {
    title: string;
    content: { label: string; value: string[] }[];
    /** 确认按钮的文案和跳转链接 */
    buttonInfo?: {
        name?: string;
        linkUrl?: string;
    };
};

/**
 * 餐损信息 - 模块
 * @wiki https://km.sankuai.com/page/1317841491#id-2.10%E9%A4%90%E6%8D%9F%E8%B5%94%E4%BB%98
 * @yapi https://yapi.sankuai.com/project/20566/interface/api/989189
 */
type CansunInfo = {
    canSunRecords: CanSunRecordsItem[];
    /** 申请赔付按钮vo */
    applyCompensationButtonVo: {
        /** 申请餐损截止时间 */
        applyEndTime: number;
        /** 是否展示申诉赔付按钮 */
        isShow: boolean;
        /** 按钮文案 */
        context: string;
        /** 失效描述文案 */
        invalidDesc: string;
        /** 未生效描述文案 */
        effectiveDesc: string;
    };
    /** 查看账单按钮Vo */
    viewBillButtonVO: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮文案 */
        context: string;
        /** 最后一条餐损信息创建Unix时间 */
        statusUnixCtime: number;
    };
    /** 餐损状态 */
    canSunStatus: number;
    /** 取消申请赔付按钮 */
    cancelApplyButtonVo: {
        isShow: boolean;
        /** 按钮文案 */
        context: string;
    };
};
type CanSunRecordsItem = {
    /** 餐损状态描述 */
    statusDes: string;
    /** 本条餐损信息创建时间 */
    cTime: string;
    /** 原因 */
    reason: string;
    /** 提交申请餐损赔付后提醒文案 */
    cansunpromptDesc: string;
    /** 餐损状态 */
    statusCode: number;
    /** 本条餐损信息创建Unix时间 */
    statusUnixCtime: number;
    /** 餐损信息申诉截止时间 */
    applyEndTime: number;
};

/**
 * 催单 - 模块
 * @wiki https://km.sankuai.com/page/1317841491#id-2.6%E5%82%AC%E5%8D%95%E4%BF%A1%E6%81%AF
 * @yapi https://yapi.sankuai.com/project/20566/interface/api/987988
 */
type ReminderInfo = {
    /** 最后一次催单id */
    reminderId: number;
    /** 总催单次数 */
    reminderCount: number;
    /** 催单记录 */
    reminderOrderList: ReminderOrderItem[];
};
type ReminderOrderItem = {
    /** 催单id */
    reminderId: number;
    /** 用户发起催单时间戳 */
    reminderTimeFmt: number;
    /** 订单是否有效 */
    stale: number;
    /** 催单回复内容 */
    responseContent: string;
    /** 催单回复时间戳 */
    responseTimeFmt: string;
};

//四轮手动呼叫汽车配送类型
export enum fourWheelDeliveryButtonType {
    //选择车型
    SELECT = 1,
    //追加车型
    ADD = 2,
}

/** 配送标签展示样式类型 */
export enum LogisticsTagStyleType {
    /** 不展示 */
    NONE = -1,
    /** 通用样式 */
    GENERAL = 0,
    /** 蛋糕送样式 */
    CAKE = 1,
    /** 美团直送样式 */
    PERSONAL = 2,
}
/** 商家无法履约订单触发取消配送提示类型，https://km.sankuai.com/collabpage/2125377062#b-23a04d107ec04aaeab491f175d215182 */
export enum ResendShippAfterInterventionInfoType {
    /** 骑手多次取消后系统第一次干预，重发配送 */
    RIDERS_CANCEL = 0,
    /** 是否发起四轮配送 */
    FOUR_ROUNDS_DELIVERY = 1,
    /** 商家无餐异常状态时（27/28/29） ，商家确认是否无餐 */
    CHECK_NO_MEAL = 2,
    /** 商家无货(expcetionType27)状态下是否重新发起配送 */
    NO_MEAL = 3,
}

/**
 * 配送 - 模块
 * @wiki https://km.sankuai.com/page/1317841491#id-2.3%E9%85%8D%E9%80%81%E4%BF%A1%E6%81%AF
 * @yapi https://yapi.sankuai.com/project/20566/interface/api/1009441
 */
type LogisticsInfo = {
    /** 配送区块是否展示 */
    isShow: boolean;
    /** 配送基础信息 */
    basicVo: LogisticsBasicVo;
    /** 配送标题 */
    title: string;
    /** 配送标签 */
    tagVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 标签文案 */
        name: string;
        /** 是否可以点击 */
        isCanClick: boolean;
        styleType: LogisticsTagStyleType;
    };
    /** 配送计时器VO */
    timerVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 倒计时时间戳 */
        serverTime: number;
    };
    /** 查看骑手电话 */
    phoneIconVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 是否失效订单 order.status == 8（已完成）|| order.status == 9（已取消） */
        isInvalidOrder: boolean;
        /** 隐私号超时时间 */
        privacyOutOfTime: number;
        /** 查看电话文案 */
        content: string;
    };
    /** 是否展示电话图标 */
    isShowPhoneIcon: boolean;
    /** 是否展示位置图标 */
    isShowLocationIcon: boolean;
    /** 配送异常文案 */
    warningTipVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 异常文案 */
        desc: string;
        /** "详情"按钮文案,为空时不展示"详情"按钮 */
        detail: string;
        /** 压单异常文案 */
        slowOrderPressureInfo: string;
        /** 出餐后调度压单文案 */
        completeMealPressureInfo: string;
        /** 预订单压单文案 */
        preOrderPressureInfo: string;
        /** 异常code */
        exceptionType: DispatchExceptionCode;
        /** 无法履约取消配送倒计时的tip提示 */
        resendShippAfterInterventionInfo: {
            show: boolean;
            /** 0:【初次干预】时的异常单处理,重发配送  1.四轮车配送，2 商家无餐异常状态时（ 27/28/29） ，商家确认是否无餐3 27状态下是否重新发起配送 */
            type: ResendShippAfterInterventionInfoType;
            /** 倒计时模块 */
            countDown: {
                /** 是否显示倒计时模块 */
                show: boolean;
                /** 倒计时结束的时间戳 */
                resendShippTime: number;
                /** 倒计时文案 */
                text: string;
            };
            /** 拒绝配送按钮 */
            rejectButton: {
                show: boolean;
                text: string;
                /** 弹窗后文案配置 */
                dialogTitle: string;
                dialogContent: string;
            };
            /** 继续配送按钮 */
            agreeButton: {
                show: boolean;
                text: string;
                /** 弹窗后文案配置 */
                dialogTitle: string;
                dialogContent: string;
            };
            /** 提示文案 */
            showContent: string;
        };
        /**
         * （无人接单预期管理）
         * （点击异常提示条时展示弹窗）
         * 无骑手接单建议说明
         */
        descTips?: {
            question: string;
            answer: string;
        }[];
        /**
         * [多运单二期](https://km.sankuai.com/collabpage/2060793917)新增的提示条。
         *
         * ![](https://msstest.sankuai.com/waimaie/fecore_src_diagram/suggestOperationRemindInfo.jpg|height=60)
         */
        suggestOperationRemindInfo?: {
            show: boolean;
            button?: {
                show: boolean;
                text: string;
                buttonLabel: string;
            };
            showContent: string;
        };
        /**
         * [多运单二期](https://km.sankuai.com/collabpage/2060793917)新增的灰字提示文案。例如【处理建议及预估餐损 >】。
         */
        suggestButton?: {
            show: boolean;
            text: string;
            bidMc: string;
            popWindow?: {
                title: string;
                contents?: {
                    title: string;
                    content: string;
                }[];
                buttonText: string;
            };
        };
    };
    /** 点击电话按钮弹出的无人机的文案 */
    uavDeliveryTip: string;
    /** 配送进度 */
    logisticsProcessVo: {
        /** 进度时间 */
        formatTime: string;
        /** 进度描述 */
        processDesc: string;
    };
    /** 延迟分配骑手时间(压单时间:分钟) */
    delayPushVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 标题:预计耗时 */
        delayPushTitle: string;
        /** 压单时间 */
        delayPushMin: number;
        /** 单位:分钟 */
        unit: string;
    };
    /** 配送权益 */
    logisticsRightVo: {
        /** 权益图标 */
        rightsIcon: string;
        /** 权益文案 */
        rightsContent: string;
        /** 文案颜色 */
        rightsColor: string;
        /** 文案背景颜色 */
        rightsBgColor: string;
        /** 权益是否生效 */
        rightValid: boolean;
    };
    /** 加小费 */
    addFeeVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 加小费文案 */
        title: string;
        /** 加小费金额 */
        tipFee: number;
        /** 小费金额单位 */
        unit: string;
        /**
         * 是否展示右上角「试一试」角标。
         * 该角标 5min 骑手未接单时展示，点击后消失。
         * 注：此字段用于控制开始出现的时机（即分配骑手时间到达 5min 时从无到有），点击之后不再展示的逻辑由前端缓存维护。
         */
        isShowTryButton: boolean;
    };
    /** 配送费VO */
    deliveryFeeVo: DeliveryFeeVo;
    /** 跑腿预计分配骑手等待时间 */
    riderExpectAcceptTimeVo: {
        /** 是否展示 */
        isShow: false;
        /** 跑腿-是否调用查询骑手排队信息接口 */
        hasRiderPressure: boolean;
        /** 跑腿-骑手预计接单时间 */
        riderExpectAcceptTime: number;
        /** 跑腿-预计接单时长上限（分钟） */
        riderEATLimit: number;
        /** 骑手预计接单时间文本 */
        riderExpectAcceptText: string;
    };
    /** 催取餐按钮 */
    urgeFoodButtonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮状态 */
        buttonStatus: UrgeBtnType;
        /** 是否更换过骑手 */
        riderHasChanged: boolean;
        /** 可催取餐的unix时间戳，单位秒 */
        availableTime: number;
    };
    /** 上报异常按钮（投诉骑手） */
    reportExceptionButtonVo: ButtonVo;
    /** 评价骑手 */
    evaluateRiderButtonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮文案 */
        content: string;
        /** 评价骑手按钮tip文案 */
        buttonTip: string;
        /** 评价骑手按钮展示
         * 0-不展示 1-待评价（新） 2-已评价(旧) 3-已评价(新) 4-待评价（旧）
         */
        buttonType: EvaluateButtonType;
        /** 是否展示评价骑手Tip 1展示2不展示 */
        iconType: 1 | 2;
        /**新增字段：orderType：订单评价类型：0：默认骑手，1：无人机 ，(后续拓展...)*/
        orderType: number;
    };
    fourWheelDeliveryButtonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮文案 */
        content: string;
        /** 1 汽车配送，2 追加车型 */
        type: 1 | 2;
        /** 子文案 */
        subContent: string;
    };
    /** 转自配按钮 */
    changeSelfLogisticsButtonVo: ButtonVo & {
        /** 0 正常展示；2 禁用样式，点击弹 Toast */
        displayType: 0 | 2;
        displayToast: string;
        /** 灵活转自配灰度 */
        flexibleSelfLogisticsGray: boolean;
    };
    /** 取消配送按钮 */
    cancelLogisticsButtonVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 按钮文案 */
        content: string;
        /** 取消配送是否锁定 */
        dispatchCancelIsLock: boolean;
        /** 下单后（取消配送）的锁定时间【目前跑腿订单使用】 0:是不锁定 -1:是锁定 number:是锁定时间 */
        dispatchCancelLockTime: number;
        /** 是否为破峰企客配送 ：0 非企客  1企客 */
        isQikeThirdDistributor: 0 | 1;
    };
    /** 加小费按钮 */
    addFeeButtonVo: ButtonVo;
    /** 发配送按钮 -  只有"自己送餐"按钮时，不下发 */
    sendDistributionButtonsVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 商家最后一次成功手动发单时使用的配送方式信息 */
        lastUse: DistributorItem;
        /** 配送按钮列表 */
        distributorList: DistributorItem[];
        /** 三方配异步操作超时时间 仅pc用（秒） */
        thirdDispatchAsyncTime: number;
        /** 众包资源位信息和权益 */
        zbResourceInfoListAndRights?: {
            /** 开始展示的时间戳 */
            startTime: number;
            /** 结束展示的时间戳 */
            endTime: number;
            /** 图片地址 */
            picUrl: string;
            /** 图片类型：0资源位 1权益 */
            type: 0 | 1;
        }[];
        /** 众包配送页版本 */
        zbPreviewVersion: number;
    };
    /** 骑手预计送达 */
    riderExpectedDeliveryVo: {
        /** 是否展示 */
        isShow: boolean;
        /** 骑手预计送达时间开始时间 */
        riderDeliveredStartTime: number;
        /** 骑手预计送达时间截止时间 */
        riderDeliveredEndTime: number;
    };
    /**
     * （无人接单预期管理）
     * 无人接单异常原因
     */
    dispatcherLessReason?: string;
    /**
     * （无人接单预期管理）
     * 餐损规则展示入口文案
     */
    noDispatcherPreConfirmEntry?: string;
    /** 是否使用新的小费编辑弹窗 */
    isNewTipFee: boolean;
    /** 换骑手提示 */
    manageExpectationsVo: {
        isShow: boolean;
        /** 当前文案 */
        currentMessage: manageExpectationsMessageVo;
        allMessages: manageExpectationsMessageVo[];
    };
    /** 联系客服按钮 */
    customerServiceButtonVo: ButtonVo;
};

type manageExpectationsMessageVo = {
    /** 当前文案 */
    message: string;
    /** 用于文案里的倒计时展示 */
    deadlineTime: number;
    /** 是否展示？icon */
    tipType: 0 | 1;
    /** 第二行文案 */
    comments: string;
    /** 下一个文案 */
    nextMessage: string;
    /** 下一个文案的展示时间 */
    nextArrivalTime: number;
};

/** 评价骑手按钮展示
 * 0-不展示 1-待评价（新） 2-已评价(旧) 3-已评价(新) 4-待评价（旧）
 */
/** 评价骑手状态 */
enum EvaluateButtonType {
    /** 不展示 */
    NONE = 0,
    /** 待评价（新样式） */
    PRE_EVALUATION_NEW = 1,
    /** 已评价(旧) */
    EVALUTION_DONE = 2,
    /** 已评价(新) */
    EVALUTION_DONE_NEW = 3,
    /** 待评价（旧） */
    PRE_EVALUATION = 4,
}

/** 配送基础信息 */
type LogisticsBasicVo = {
    /** 配送id */
    id: number;
    /** 配送状态 */
    status: LogisticsStatusCode;
    /** 取餐时间 */
    fetchTime: number;
    /** 配送员信息 */
    dispatcherVo: {
        /** 姓名 */
        name: string;
        /** 手机号码 */
        mobile: string;
        /** 头像 */
        icon: string;
        role: string;
        /** 配送员类型 0:专送骑手 1:快送骑手(专送混合配送(X配送)) -1:初始默认 */
        type: 0 | 1 | -1;
        /** 骑手类型名称 */
        typeName: string;
    };
    /** 运单id */
    dispatchOrderId: string;
    /** 配送CODE */
    code: LogisticsTypeCode;
    /** 配送类型 */
    type: number;
    /** 分配骑手时间 */
    assignTime: number;
    /** 默认配送协议 0：默认 1：跑腿优先 2：聚合配送优先 */
    defaultPushType: 0 | 1 | 2;
    /** 是否支持落地配协议 */
    supportUnionDispatcher: boolean;
    /** 骑手送餐时间时间戳 */
    sendTime: number;
    /** 配送类型 */
    deliveryPushType: number;
    /**
     * （无人接单预期管理）
     * （用于判断应该使用的样式）
     * 分配骑手用时所处的区间
     *
     * - `1` [1min, 15min) (App 为了减少动效对低端机的影响，从 1min 开始才展示，PC 保持一致)
     * - `2` [15min, 30min)
     * - `3` [30min, +∞)
     */
    animationType: -1 | 1 | 2 | 3;
    /**
     * 订单所属门店的配送方式是否满足[无人接单预期管理](https://km.sankuai.com/collabpage/1354163716)需求范围。
     *
     * Note:
     * 1. 原来是通过 grayAB 判断的（token: 2b98127a-ba37-4b40-be92-717a5f6c7029），导致同笔订单在单店和多店展示不一致。
     * 2. 多店下不请求 `DispatchPseudoMap` 组件中的天气数据，因此单店和多店的展示依然有不一致的地方。
     */
    ifSupportNoDispatcherGray: boolean;
};
/** 配送费明细 */
type DeliveryFeeVo = {
    /** 是否展示 */
    isShow: boolean;
    /** 配送费文案Title */
    title: string;
    /** 配送费金额 */
    payAmount: number;
    /** 配送费金额单位 */
    unit: string;
    /** 加小费金额 */
    tipFee: number;
    /** 优惠券金额 */
    couponAmount: number;
    /** 支付方式: 枚举值：订单款、配送账户 */
    payType: string;
    /** 是否展示配送费详情 */
    isShowDeliveryFeeDetail: boolean;
    /** 定价明细 */
    pricingDetails: {
        /** 定价明细费用名称 */
        pricingName: string;
        /** 定价明细附加内容 */
        pricingSubName: string;
        /** 定价明细费用金额 */
        pricingValue: number;
        /** 原价（划线价） */
        originPriceValue?: number;
        /** 减免金额 */
        deductionPriceValue?: number;
        /** 减免描述 */
        deductionDescription?: string;
    }[];
    /** 惊喜立减金额 */
    bmlAmount: number;
};
/** 配送按钮展示VO */
type ButtonVo = {
    /** 是否展示 */
    isShow: boolean;
    /** 按钮文案 */
    content: string;
};
/** 权益信息 */
type LogisticsRightVo = {
    /** 权益图标 */
    rightsIcon: string;
    /** 权益文案 */
    rightsContent: string;
    /** 文案颜色 */
    rightsColor: string;
    /** 文案背景颜色 */
    rightsBgColor: string;
    /** 权益是否生效 */
    rightValid: boolean;
};

/** 催取餐按钮状态 */
enum UrgeBtnType {
    /** 不展示 */
    HIDE = 0,
    /** 置灰 */
    DISABLED = 1,
    /** 正常展示 */
    SHOW = 2,
    /** 已催取 */
    URGE_DOWE = 3,
}
/** 配送按钮信息 */
interface DistributorItem {
    /** 配送方式ID */
    logisticsId: number;
    /** 商家的配送方式code */
    logisticsCode: string;
    /** 商家的配送方式名称 */
    logisticsName: string;
    /** 商家的配送方式类型 */
    logisticsType: number;
    /** 优先级 */
    priority: number;
    /** 三方运力具体的配送方式id */
    specificLogisticsId: number;
    /** 三方运力具体的配送方式code */
    specificLogisticsCode: string;
    /** 服务商code */
    carrierCode: string;
    /** 服务商名称 */
    carrierName: string;
    /** 服务商-服务包名称 */
    carrierConnectLogisticsName: string;
    /** 展示的小标标识类型，0=不展示 1=展示跑腿小标 */
    smallMarkType: 0 | 1;
    /** 按钮展示类型：0正常显示，1置灰不可点击，2正常显示但点击时toast提示 */
    displayType: 0 | 1 | 2;
    /** （displayType = 2 时）点击按钮时的 Toast 文案 */
    displayToast?: string;
    /**
     * 门店支持的跑腿业务线的所有服务品牌配送code，逗号分隔 eg："1003,2011" ,"1003"
     * 非跑腿业务线，此字段无业务含义
     */
    zbLogisticsCodes: string | null;
}

/**
 * 其他
 */
/** 餐损险保障详情信息 */
type InsuranceRefundAdviceType = {
    /** 标题 */
    title: string;
    /** 自主售后理赔金额 */
    expectMoney?: number;
    /** 内容 */
    content: Array<{
        label: string;
        value: string;
    }>;
    /** 详情表格图片 */
    refundRule: {
        /** 标题 */
        name: string;
        /** 图片地址 */
        detailPic: string;
    };
    /** 保障条款地址 */
    specUrl: string;
};
/** 评价骑手结果 */
type DispatcherEvaluationResult = {
    /** 商家输入的评价文案 */
    personal: string;
    /** 结构化的评价文案 */
    structureds: string[] | null;
    /** 评价分属 */
    grade: number;
    /** 订单viewId */
    wmOrderViewId: string;
};
/** 跑腿排名信息 */
type IPTRiderRankingOrder = {
    /** 订单id */
    orderViewId: number;
    /** 排名文案 */
    pressure: string;
    /** 是否展示解释icon */
    type: 0 | 1;
};
/** 查询骑手排名接口入参 */
type IPTRiderRankingParams = {
    orderViewId: string;
    /** 分配骑手时间 */
    assignTime: number;
    /** 骑手预计接单时间 */
    acceptTime: number;
    wmPoiId: number;
};
/** 骑手排名信息 */
type IorderPTRiderRankingInfo = {
    /** 轮询间隔 */
    intervalSec: number;
    orders: IPTRiderRankingOrder[];
};
/** 结算信息项 */
type SettlementItem = {
    /** 是否展示问号 */
    showRemind: boolean;
    /** 问号展示的位置，0跟在收费名称后边，1跟在费用后边 */
    showRemindStyle: 0 | 1;
    /** 分割线样式，0不分割，1上方分隔，2下方分隔，3上下都分隔 */
    separateStyle: 0 | 1 | 2 | 3;
    /** 费用计算公式 */
    calculationRules: string;
    /** hover 问号时展示的文案(目前仅有青山计划用到) */
    toast: string;
    /** 收费项名称 */
    chargeName: string;
    /** 弹框标题文案（如果有） */
    popupTitle: string;
    /** 收费项扩展文案（红色高亮） */
    chargeNameExtend?: string;
    /** 收费项金额 */
    totalCharge: string;
    /** 弹窗的总收费名称 */
    popupTotalChargeName: string;
    /** 弹窗收费的总金额 */
    popupTotalCharge: string;
    /** 弹框副标题 */
    popupSubTitle: string;
    /** 弹窗收费的(副)总金额，主要展示划线价 */
    popupTotalChargeExtension: string;
    /** 弹窗收费的(副)总金额样式， 0为正常格式，1为中划线*/
    popupTotalChargeExtensionStyleId: string;
    /** 收费规则 */
    chargeRules: string;
    /** 收费规则对应的名称 */
    chargeRulesName: string;
    /** 收费项明细列表 */
    item: ChargeItem[];
    /** 样式id */
    styleId: string;
    /** 子收费项（PHF：商品金额合计、商家费用合计） */
    subSettleDynamicList?: SettlementItem[];
};

/**
 * 跑腿商家【门店维度】权益信息
 */
type PaotuiPoiRightsInfo = {
    /** 权益图标 */
    buttonImg: string | null;
    /** 权益图标 */
    tipsIcon: string | null;
    /** 权益文案 */
    tipsContent: string | null;
    /** 权益文案颜色 */
    tipsColor: string | null;
    /** 权益文案背景颜色 */
    tipsBgColor: string | null;
    /** 权益描述浮层文案 */
    tipsComment: string | null;
    /** 资源类型  0.资源位 1.权益 */
    type: 0 | 1;
    /** 权益资产id，发起配送时使用此字段 */
    rightDisToken?: string;
};
/**
 * 卡餐上报弹框的展示类型，和[上报卡餐接口](https://km.sankuai.com/page/735683189#id-1%E3%80%81%E6%96%B0%E5%8D%A1%E9%A4%90%E4%B8%8A%E6%8A%A5%E6%8E%A5%E5%8F%A3(%E4%B8%8B%E6%B2%89%E7%BD%91%E5%85%B3%EF%BC%8C%E5%8E%9F/w/report/waitMeal))里的 sceneType 字段保持一致
 */
enum ReportMakeMealSlowModalDisplayType {
    /** 大额订单 */
    BIG_ORDER = 1,
    /** 多sku订单 */
    MULTI_SKU = 2,
    /** 短时间内进单量过大 */
    SHARK_ORDER = 3,
    /** 已识别出卡餐（骑手在店内等餐时间过长） */
    RIDER_WAIT = 4,
    /** 主动上报 */
    MANUAL_REPORT = 5,
    /** 其他卡餐场景 */
    OTHER = 30,
}

/** 卡餐上报类型 */
enum DispatchType {
    /** 出餐完成后分配骑手 */
    AFTER_COOK = 'dispatchAfterCook',
    /** 立即延长出餐时长 */
    IMMEDIATELY = 'dispatchImmediately',
}
/** 卡餐上报类型 */
enum Degree {
    /** 轻微卡餐 */
    SLIGHT = '11',
    /** 严重卡餐 */
    SERIOUS = '12',
}

enum TimeSelectType {
    /** 延长出餐时间 */
    extend = 1,
    /** 措施生效时间 */
    effect = 2,
}

/** 餐损险保险状态枚举 */
enum InsuranceStatusCode {
    /** 未知状态 兜底 */
    UNKNOWN = 0,
    /** 理赔中 */
    CLAIMING = 100,
    /** 理赔成功 */
    CLAIM_SUCCESS = 200,
    /** 理赔失败 */
    CLAIM_FAIL = 300,
}

/** 卡餐push消息里包含的订单信息 */
type MakeMealSlowPushOrderInfo = {
    /** 订单流水号 */
    orderSeq: number;
    /** 预计送达时间戳（单位：秒） */
    estimateArrivalTime: number;
    /** 商品种类数量 */
    skuTypeCount: number;
    /** 商品数量 */
    skuCount: number;
    /** 预计收入 */
    settleAmount: string;
    /** 超时时间，上报卡餐时需要 */
    expireTime: string;
    /** 来自push的订单ID，用于埋点上报 */
    orderId: string;
    /** 待出餐个数 */
    prepMealOrderCount: number;
    /** 待出餐流水号拼串 */
    prepMealOrderSeqList: string;
    /** 来自Push的推荐延长出餐时间数值 */
    recommendEffectTimeValue: number;
    /** 后端模型灰度标识，push上报卡餐时传回去 */
    balanceUuid: string;
    /** 订单ViewID */
    orderViewId: string;
    /** 订单标识  1大订单，0无 */
    tag: '1' | '0';
};

/** 卡餐上报类型 */
enum ReportMakeMealSlowType {
    /** 主动上报 */
    INITIATIVE_REPORT = 1,
    /** 被动上报 */
    PASSIVE_REPORT = 2,
}

/** 餐损申诉页面信息 */
type IresponsibilityAppeal = {
    /** 一级申诉类型 */
    appealTypeName: string;
    /** 申诉类型提示 */
    typeTips: string;
    /** 用于级联组件的参数 */
    value?: number;
    label?: string;
    /** 二级申诉 */
    reansons: {
        /** 二级申诉类型 */
        appealReason: string;
        /** 举证图片提示 */
        picturesTips: string;
        /** 图片是否必传 */
        mustvaule: 0 | 1;
        /** 用于级联组件的参数 */
        value?: number;
        label?: string;
    }[];
}[];

/** 用户提单时的配送方式 */
enum ShippingService {
    /** 默认值 */
    DEFAULT = 0,
    /** 商家自配 */
    ZIPEI = 1,
    /** 美团专送 */
    ZHUAN_Z = 1010,
    /** 美团快送 */
    KUAISONG = 1020,
    /** 美团混合送 */
    HUNHE = 1030,
    /** 美团全城送 */
    QUANCHENG = 1040,
    /** 众包服务 */
    ZHONGBAO = 2010,
    /** 三方配送 */
    SANFANG = 300,
    /** 企客-全城送 */
    QIKE_CITY = 4015,
}

type FeedBackResp = {
    /** 是否展示投诉模块 */
    isShow: boolean;
    /** 骑手投诉入口 */
    feedBackUrl: string;
    /** 骑手投诉信息列表 */
    feedbackList: {
        /** 意见反馈记录ID */
        feedbackId: number;
        /** 详情跳转URL */
        detailUrl: string;
        /** 处理状态， 0: 处理中，1： 已处理 */
        replayFlag: 0 | 1;
        /** 状态文案：{工单骑手姓名}投诉处理中，请您耐心等待 */
        statusContent: string;
    }[];
};

type EvaluationInfoListItem = {
    /** 骑手顺序描述 */
    dispatcherOrderDesc: string;
    /** 骑手名称 */
    dispatcherName: string;
    /** 状态标签名称，服务中|已完成|已取消 */
    tagStatusName: string;
    /** 标签状态：1-服务中|2-已完成|3-已取消|-1-未知 */
    tagStatus: -1 | 1 | 2 | 3;
    /** 标签状态，1-骑手已转单;2-骑手已取消;3-骑手已送达;4-骑手仍在服务中 */
    serviceStatus: number;
    /** 开始服务|开始服务|服务已取消|订单已送达 */
    serviceStatusDesc: string;
    /** 服务时间 */
    serviceTime: string;
    /** 骑手id */
    dispatcherId: number;
    /** 评价标签 */
    structuredList: [string];
    /** 手动填写的其他评价内容 */
    personal: string;
    /** 1-不满意；5-满意；-1-未选择 */
    grade: 1 | 5 | -1;
    /** 配送单ID */
    dispatchOrderId: number;
    /** 配送单ID string , 防止直接改动dispatchOrderId类型对app造成影响所以pc新增此字段*/
    dispatchOrderIdStr: string;
    /** 骑手类型 0-普通 1-跑腿最后一名骑手 */
    riderType: 0 | 1;
    /** 骑手接单记录号 */
    index: number;
    /** 运单id */
    deliveryId: string;
    /** 是否已拉黑 */
    blacklisted: 0 | 1;
};

type EvaluationResp = {
    /** 评价信息 */
    evaluationInfoList: EvaluationInfoListItem[];
    /** 评价子标题 */
    subTitle: string;
};

type EvaluationTagInfo = {
    /** 好评标签列表 */
    goodTagInfoList: {
        code: number;
        tagDesc: string;
    }[];
    /** 差评标签列表 */
    badTagInfoList: {
        code: number;
        tagDesc: string;
    }[];
};

/** 评价详情-新 */
type LogisticsEvalutionResponse = {
    /** 投诉信息 */
    feedBackResp: FeedBackResp;
    /** 评价信息 */
    evaluationResp: EvaluationResp;
    /** 评价标签信息 */
    evaluationTagInfo: EvaluationTagInfo;
    /** 是否展示拉黑入口 */
    isShowBlacklist: boolean;
};

/** 评价骑手接口入参-新 */
interface SaveDispatcherEvaluationParams {
    wmOrderViewId: string;
    wmPoiId: number;
    /** 是否满意 -1 未选择 | 1-不满意 5-满意 */
    grade: -1 | 1 | 5;
    /** 评价标签,多个标签之间逗号隔开 */
    structureds: string;
    /** 其他评价内容 */
    personal: string;
    /** 骑手id */
    dispatcherId: number;
    /** 骑手姓名 */
    dispatcherName: string;
    /** 图片URL,多个url之间逗号隔开 */
    pictureUrls: string;
    /** 配送单id */
    dispatchOrderId: string;
    /** 骑手接单记录号 */
    index: number;
    /** 运单id */
    deliveryId: string;
}

export {
    UnprocessedOrderType,
    OrderStateTypes,
    OrderBlockTag,
    OrderBasicInfo,
    OrderDetailInfo,
    FoodInfo,
    CartDetailInfo,
    ChargeInfo,
    ChargeSettlementItem,
    OrderStatusCode,
    PayTypeCode,
    PayStatusCode,
    SaleSchedulesStatus,
    ResponsibleInfo,
    ResponsibleInfos,
    RefundInfo,
    RefundRecordItem,
    RefundSuggestionVo,
    RefundSuggestions,
    FoodSafeInfo,
    FoodSafeStatusIconType,
    FoodSafeLatestStatusCode,
    DUTY_STATUS,
    RulesInfo,
    UserCompensateInfo,
    CansunInfo,
    CanSunRecordsItem,
    InsuranceRefundAdviceType,
    ReminderInfo,
    LogisticsInfo,
    LogisticsRightVo,
    LogisticsTypeCode,
    UrgeBtnType,
    LogisticsStatusCode,
    OrderUserInfo,
    DistributorItem,
    DispatcherEvaluationResult,
    RawOrderBaseInfo,
    IPTRiderRankingOrder,
    IPTRiderRankingParams,
    IorderPTRiderRankingInfo,
    SelfServiceCode,
    PaotuiPoiRightsInfo,
    ReportMakeMealSlowModalDisplayType,
    OrderTagCode,
    AllCountInfo,
    OrderCancelInsuranceSuggest,
    InsuranceStatusCode,
    SettlementItem,
    DispatchType,
    MakeMealSlowPushOrderInfo,
    ReportMakeMealSlowType,
    DispatchExceptionCode,
    OrderBusinessType,
    IresponsibilityAppeal,
    ShippingService,
    LogisticsEvalutionResponse,
    SaveDispatcherEvaluationParams,
    FeedBackResp,
    EvaluationResp,
    EvaluationTagInfo,
    EvaluateButtonType,
    EvaluationInfoListItem,
    InstantRefundJudgeInfo,
    FoodDoneInfo,
    ResponsibleDetailInfoItem,
    Degree,
    TimeSelectType,
};
